import { useState, createContext, useContext } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Lock, Sparkles, ArrowRight, Check } from "lucide-react";

const UpgradeContext = createContext();

const PLAN_FEATURES = {
  trial: {
    name: "Free Trial",
    gated: ["analytics", "team_management", "custom_branding", "advanced_scheduling", "export_data", "priority_support"]
  },
  solo: {
    name: "Solo",
    gated: ["team_management", "advanced_scheduling", "priority_support"]
  },
  team: {
    name: "Team",
    gated: []
  }
};

const FEATURE_INFO = {
  analytics: { name: "Advanced Analytics", plan: "solo", desc: "Income projections, booking trends, and client insights" },
  team_management: { name: "Team Management", plan: "team", desc: "Add team members, assign roles, and share client directory" },
  custom_branding: { name: "Custom Branding", plan: "solo", desc: "Upload your logo and customize colors" },
  advanced_scheduling: { name: "Advanced Scheduling", plan: "team", desc: "Buffer times, recurring bookings, and team availability" },
  export_data: { name: "Export Data", plan: "solo", desc: "Download your client data and booking history" },
  priority_support: { name: "Priority Support", plan: "team", desc: "Priority email and chat support" },
};

export function UpgradeProvider({ children, currentPlan = "trial", isLegacyPilot = false, isDemo = false }) {
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [targetFeature, setTargetFeature] = useState(null);

  const isFeatureGated = (featureKey) => {
    // Iter 9.38 - Legacy pilots / demos bypass ALL feature gates regardless of currentPlan tier.
    // Admin can set Shelsea to tier="solo" (later "team") and she still has full access via the
    // legacy_pilot_free exemption flag.
    if (isLegacyPilot || isDemo) return false;
    if (currentPlan === "legacy_pilot_free" || currentPlan === "legacy" || currentPlan === "pilot") return false;
    const plan = PLAN_FEATURES[currentPlan] || PLAN_FEATURES.trial;
    return plan.gated.includes(featureKey);
  };

  const promptUpgrade = (featureKey) => {
    setTargetFeature(featureKey);
    setShowUpgrade(true);
  };

  const checkAccess = (featureKey) => {
    if (isFeatureGated(featureKey)) {
      promptUpgrade(featureKey);
      return false;
    }
    return true;
  };

  const feature = targetFeature ? FEATURE_INFO[targetFeature] : null;
  const requiredPlan = feature?.plan || "solo";

  return (
    <UpgradeContext.Provider value={{ isFeatureGated, promptUpgrade, checkAccess, currentPlan }}>
      {children}
      <Dialog open={showUpgrade} onOpenChange={setShowUpgrade}>
        <DialogContent className="max-w-md" data-testid="upgrade-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Upgrade to Unlock
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {feature && (
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-4 h-4 text-gray-400" />
                  <p className="font-medium">{feature.name}</p>
                  <Badge className="text-xs capitalize">{requiredPlan}+ plan</Badge>
                </div>
                <p className="text-sm text-gray-600">{feature.desc}</p>
              </div>
            )}
            <div className="space-y-2">
              <p className="text-sm font-medium">What you get with {requiredPlan === "team" ? "Team" : "Solo"}:</p>
              {(requiredPlan === "solo" ? [
                "Advanced analytics & insights",
                "Custom branding & colors",
                "Data export",
                "Email notifications"
              ] : [
                "Everything in Solo",
                "Up to 5 team members",
                "Shared client directory",
                "Team scheduling",
                "Priority support"
              ]).map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <Check className="w-3.5 h-3.5 text-green-500" />
                  <span>{f}</span>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowUpgrade(false)}>Maybe Later</Button>
            <a href="/subscription" className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 text-sm font-medium" data-testid="upgrade-cta">
              Upgrade Now <ArrowRight className="w-4 h-4" />
            </a>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </UpgradeContext.Provider>
  );
}

export function useUpgrade() {
  return useContext(UpgradeContext);
}

export function GatedFeature({ featureKey, children, fallback = null }) {
  const { isFeatureGated, promptUpgrade } = useUpgrade();
  if (isFeatureGated(featureKey)) {
    return (
      <div
        className="relative cursor-pointer"
        onClick={() => promptUpgrade(featureKey)}
        data-testid={`gated-${featureKey}`}
      >
        <div className="opacity-50 pointer-events-none">{fallback || children}</div>
        <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-xl">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-black text-white rounded-full text-xs">
            <Lock className="w-3 h-3" /> Upgrade to unlock
          </div>
        </div>
      </div>
    );
  }
  return children;
}
